import React, { FunctionComponent, useEffect, useState } from 'react'
import axios, { AxiosResponse } from 'axios'
import { useDispatch } from 'react-redux'
import { defaultAuthHandler } from '../auth'
import { supportEmail, SupportContact } from '../components/ContactSupport/Data'
import { Customer, HostData, HostDataUtil, PmitcCustomer, Product } from '../components/Customer/Data'
import { AccessConfig } from '../components/MfNg/Access/Data'
import { getPmitcOrgRef } from '../components/Pmitc/Data'
import { PartnerData, ServiceProvider, ServiceProvidersResponse } from '../components/ServiceProviders/Data'
import { AuthType, getSupportId } from '../components/Team/Data'
import { getInitials, TeamSettingsData } from '../components/TeamSettings/TeamSettings'
import Toaster from '../components/Toaster'
import { loadAndApplyUserSettings } from '../components/UserActions/UserSettings'
import { useMountEffect } from '../hooks/MountEffect'
import { setAccessConfig } from '../redux/access'
import { setFeatureEnabled } from '../redux/features'
import { initHosts } from '../redux/hosts'
import { setEnabledProducts } from '../redux/products'
import { useTypedSelector } from '../redux/reducers'
import { setSettings } from '../redux/settings'
import { showHost } from '../redux/slideout'
import { initSupportState, SupportState } from '../redux/support'
import { HttpStatus } from '../service/HttpStatus'
import App from './App'

interface ParentsResponse {
  parents: SupportContact[]
}

interface ProductsResponse {
  products: Product[]
}

const AppContainer: FunctionComponent = () => {
  const teamID = defaultAuthHandler.getTeamID()
  const [hosts, setHosts] = useState<HostData[]>([])
  const [loading, setLoading] = useState(true)
  const activeAccessConfig = useTypedSelector(r => r.access.accessConfig)
  const activeSlideout = useTypedSelector(r => r.slideout)
  const [nextAccessConfig, setNextAccessConfig] = useState<AccessConfig>()
  const isAccessFrameActive = activeAccessConfig !== undefined
  const [confirmingCloseFrame, setConfirmingCloseFrame] = useState(false)
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([])
  const [parents, setParents] = useState<SupportContact[]>([])
  const [team, setTeam] = useState<TeamSettingsData>()
  const [partners, setPartners] = useState<PartnerData[]>([])
  const dispatch = useDispatch()

  useMountEffect(() => {
    const handleInvalidSession = (details: any) => {
      console.warn('token invalid, logging out user', details)
      defaultAuthHandler.logout()
    }

    axios.interceptors.response.use(
      response => {
        if (response.status === HttpStatus.Forbidden || response.status === HttpStatus.Unauthorized) {
          handleInvalidSession(response)
        }
        return response
      },
      err => {
        const errstring = err.toString()
        if (errstring.includes('401') || errstring.includes('403')) {
          handleInvalidSession(err)
        }
        return Promise.reject(err)
      }
    )
  })

  useEffect(() => {
    if (!isAccessFrameActive) {
      if (nextAccessConfig) {
        dispatch(setAccessConfig(nextAccessConfig))
      }
      exitConfirmClosePrompt()
    }
  }, [isAccessFrameActive, nextAccessConfig, dispatch])

  const loadCustomers = async () => {
    try {
      const result = await axios.get<{ customers: HostData[] }>(`/api/teams/${teamID}/customers`)
      setHosts(result.data.customers || [])
      dispatch(initHosts(result.data.customers || []))
    } catch (_) {
      Toaster.notifyFailure('Unable to load customers.')
    }
  }

  useMountEffect(() => {
    Promise.all([
      loadCustomers(),
      axios.get<TeamSettingsData>(`/api/teams/${teamID}/settings`).then(res => {
        setTeam({
          ...res.data,
          initials: res.data.initials || getInitials(res.data.name),
          defaultAuthType: res.data.defaultAuthType || AuthType.Password,
        })
        dispatch(
          setSettings({
            demo: res.data.demoMode ?? false,
          })
        )
      }),
      axios.get<string[]>(`/api/teams/${teamID}/feature`).then(r => {
        r.data.forEach(f => dispatch(setFeatureEnabled(f, true)))
      }),
    ]).finally(() => setLoading(false))

    const customerPoll = setInterval(() => loadCustomers(), 2 * 60 * 1000)

    return () => clearInterval(customerPoll)
  })

  useMountEffect(() => {
    axios
      .get<ProductsResponse>(`/api/teams/${teamID}/products`)
      .then(res => dispatch(setEnabledProducts(res.data.products)))
      .catch(err => console.log('unable to load products: ' + err))
  })

  useMountEffect(() => {
    axios
      .get<ServiceProvidersResponse>(`/api/teams/${teamID}/service-providers`)
      .then(res => setServiceProviders(res.data.serviceProviders))
      .catch(err => console.log('unable to load service providers: ' + err))
  })

  useMountEffect(() => {
    axios
      .get<ParentsResponse>(`/api/teams/${teamID}/parents`)
      .then(res => setParents(res.data.parents))
      .catch(err => console.log('unable to load parents: ' + err))
  })

  useMountEffect(() => {
    axios
      .get<PartnerData[]>(`/api/teams/${teamID}/partners`)
      .then(res => setPartners(res.data))
      .catch(err => console.log('unable to load partners: ' + err))
  })

  useMountEffect(() => {
    loadAndApplyUserSettings(defaultAuthHandler.getToken()?.accessToken ?? '')
  })

  useMountEffect(() => {
    axios.get<SupportState>(`/api/teams/${teamID}/support-versions`).then(res => {
      dispatch(initSupportState(res.data))
    })
  })

  const openConfirmClosePrompt = () => setConfirmingCloseFrame(true)

  const exitConfirmClosePrompt = () => {
    setConfirmingCloseFrame(false)
    setNextAccessConfig(undefined)
  }

  const openAccessFrame = (config: AccessConfig) => {
    if (isAccessFrameActive && activeAccessConfig !== config) {
      setNextAccessConfig(config)
      openConfirmClosePrompt()
      return
    }

    dispatch(setAccessConfig(config))
  }

  const closeConfirmed = () => dispatch(setAccessConfig(undefined))

  const addCustomer = async (product: string, customer: Customer | PmitcCustomer): Promise<boolean> => {
    const path = HostDataUtil.isPmitc(product) ? 'pmitc/customers' : 'customers'
    const url = `/api/teams/${teamID}/${path}`
    return axios
      .put<any>(url, customer)
      .then(resp => {
        const hostList = hosts === null ? [] : hosts
        setHosts([resp.data].concat(hostList))
        Toaster.notifySuccess(`${resp.data.name} added.`)
        return true
      })
      .catch(e => {
        notifyAddCustomerFailure(product, customer, e)
        return false
      })
  }

  const deleteCustomer = (customerID: string): void => {
    axios
      .delete(`/api/teams/${teamID}/customers/${customerID}`)
      .then(() => {
        let hostList = hosts === null ? [] : hosts
        hostList = hostList.filter(host => {
          if (host.id !== customerID) {
            return host
          }
          Toaster.notifySuccess(`${host.name} removed.`)
          return null
        })
        setHosts(hostList)
      })
      .catch(_ => Toaster.notifyFailure('Unable to remove customer.'))
  }

  const editCustomer = async (product: string, customer: Customer, customerID?: string): Promise<boolean> => {
    return axios
      .put<Customer, AxiosResponse<HostData>>(`/api/teams/${teamID}/customers/${customerID}`, customer)
      .then(resp => {
        let hostList = hosts === null ? [] : hosts
        hostList = hostList.map(host => {
          if (host.id !== customerID) {
            return host
          }
          return resp.data
        })
        setHosts(hostList)

        if (activeAccessConfig && activeAccessConfig.hostID === customerID) {
          const config = activeAccessConfig
          config.hostName = customer.name
          dispatch(setAccessConfig(config))
        }

        if (activeSlideout.host) {
          dispatch(
            showHost({
              ...activeSlideout.host,
              name: customer.name,
              tags: customer.tags,
              infrastructure: customer.infrastructure,
            })
          )
        }
        Toaster.notifySuccess(`${resp.data.name} updated.`)
        return true
      })
      .catch(e => {
        console.log(e)
        Toaster.notifyFailure('Unable to update profile.')
        return false
      })
  }

  const modifyTeamSettings = async (newSettings: TeamSettingsData) => {
    return axios
      .put<TeamSettingsData>(`/api/teams/${teamID}/settings`, newSettings)
      .then(_ => {
        setTeam(newSettings)
        return Promise.resolve(true)
      })
      .catch(_ => {
        console.log('failed to update team details')
        return Promise.reject(false)
      })
  }

  const notifyAddCustomerFailure = (product: string, customer: Customer | PmitcCustomer, e: any) => {
    let messageContent = <span>Unable to add customer.</span>
    if (HostDataUtil.getProductText(product) && e.response) {
      const pmitcCustomer = customer as PmitcCustomer
      if (pmitcCustomer.existingOrg && e.response?.status === HttpStatus.Conflict) {
        const subject = `Request to connect organization ${getPmitcOrgRef(pmitcCustomer)} to team ${getSupportId(
          teamID
        )}`
        messageContent = (
          <span>
            Unable to add customer.
            <br />
            Contact <a href={`mailto:${supportEmail}?subject=${subject}`}>PaperCut Multiverse support</a> to connect
            this organization.
          </span>
        )
      } else if (e.response.data) {
        const responseData = e.response.data.trim()
        switch (responseData) {
          default:
            messageContent = <span>Unable to add customer: {responseData}.</span>
            break
          case 'unknown-crn':
            messageContent = <span>Unable to add customer: check that the CRN is correct.</span>
            break
          case 'not-a-service-provider':
          case 'inactive-service-provider':
            messageContent = (
              <span>
                Unable to add customer.
                <br />
                You need to be an active service provider to be able to add customers.
              </span>
            )
            break
        }
      }
    }

    Toaster.notifyFailureContent(messageContent)
  }

  if (loading) return <></>

  return (
    <App
      teamID={teamID}
      team={
        team === undefined
          ? {
              name: '',
              publicName: '',
              supportEmail: '',
              defaultAuthType: AuthType.Password,
            }
          : team
      }
      hosts={hosts}
      slideout={activeSlideout}
      activeAccessConfig={activeAccessConfig}
      isAccessFrameActive={isAccessFrameActive}
      confirmingCloseFrame={confirmingCloseFrame}
      addCustomer={addCustomer}
      deleteCustomer={deleteCustomer}
      editCustomer={editCustomer}
      openAccessFrame={openAccessFrame}
      closeConfirmed={closeConfirmed}
      exitConfirmClosePrompt={exitConfirmClosePrompt}
      openConfirmClosePrompt={openConfirmClosePrompt}
      serviceProviders={serviceProviders}
      parents={parents}
      modifyTeamSettings={modifyTeamSettings}
      partners={partners}
    />
  )
}

export default AppContainer
